import { rcApi, jwksApi } from './create-api';
import {
  rcApiResponseFormatter,
  decodeToken,
  getToken,
  isJWTExpired,
} from '@utils';
import config from '@config';

const { jwksAppName } = config;
const RC_PRODUCTION_URL = 'https://platform.ringcentral.com/restapi/v1.0';
let refreshingJwks = null;

const getCountryListUrl = '/dictionary/country';
const getStateListUrl = '/dictionary/state';
const getTimezoneListUri = '/dictionary/timezone';
const getGreetingsLanguageUri = '/dictionary/language';
const getUserGreetingListUri = '/dictionary/greeting';

export const validateJwks = async (
  token,
  accountUid,
  appName = process.env.REACT_APP_JWKS_APP_NAME
) => {
  if (isJWTExpired(token, true)) {
    refreshingJwks = refreshingJwks || getAccountJwksToken(accountUid, appName);
    const { data, error } = await refreshingJwks;

    if (data) {
      return {
        token: data,
        hasUpdatedToken: true,
      };
    }

    refreshingJwks = false;
    return { error };
  }

  refreshingJwks = false;
  return { token };
};

const fetchJwksToken = async (accountId, appName) => {
  const decodedToken = decodeToken(getToken());

  return await rcApiResponseFormatter(
    rcApi.post(
      process.env.REACT_APP_JWKS_URL,
      {
        appName,
        accountId,
      },
      {
        headers: {
          access_token: decodedToken.rc_access_token,
        },
      }
    )
  );
};

const getAccountJwksToken = async (
  accountUid,
  appName = process.env.REACT_APP_JWKS_APP_NAME
) => {
  if (process.env.REACT_APP_RC_BASE_API_URL.includes('devtest')) {
    await fetchJwksToken(accountUid, process.env.REACT_APP_JWKS_APP_NAME);
  }

  const { data, error } = await fetchJwksToken(accountUid, appName);

  if (data) {
    let exp = new Date();
    exp = exp.setSeconds(exp.getSeconds() + data.expires_in);
    exp = Math.round(exp / 1000);

    return {
      data: {
        accessToken: data.access_token,
        exp,
      },
      error,
    };
  }

  return { error };
};

const getAllExtensions = async (accountUid, token, query) => {
  return new Promise(async (resolve, reject) => {
    const extensions = [];
    let pageCounter = 1;
    const { data, error, paging } = await rcApiResponseFormatter(
      jwksApi.get(
        `${RC_PRODUCTION_URL}/account/${accountUid}/extension?perPage=1000${
          query ? `&${query}` : ''
        }`,
        {
          hasToken: token,
          accountUid,
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      extensions.push(...data);
      if (paging.totalPages > 1) {
        while (pageCounter <= paging.totalPages) {
          pageCounter += 1;
          const { data, error } = await rcApiResponseFormatter(
            jwksApi.get(
              `${RC_PRODUCTION_URL}/account/${accountUid}/extension?page=${pageCounter}&perPage=1000${
                query ? `&${query}` : ''
              }`,
              {
                hasToken: token,
                accountUid,
                headers: {
                  Authorization: `Bearer ${token && token.accessToken}`,
                },
              }
            )
          );
          if (error) {
            resolve({
              data: null,
              error,
            });
          } else {
            extensions.push(...data);
          }
        }
        resolve({
          data: extensions,
          error: null,
        });
      } else {
        resolve({
          data: extensions,
          error: null,
        });
      }
    }
  });
};

const getPhoneNumbers = async (accountUid, token, query) => {
  return new Promise(async (resolve, reject) => {
    const phoneNumbers = [];
    let pageCounter = 1;
    const { data, error, paging } = await rcApiResponseFormatter(
      jwksApi.get(
        `${RC_PRODUCTION_URL}/account/${accountUid}/phone-number?perPage=1000${
          query ? `&type=${query}` : ''
        }`,
        {
          hasToken: token,
          accountUid,
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      phoneNumbers.push(...data);
      if (paging.totalPages > 1) {
        while (pageCounter <= paging.totalPages) {
          pageCounter += 1;
          const { data, error } = await rcApiResponseFormatter(
            jwksApi.get(
              `${RC_PRODUCTION_URL}/account/${accountUid}/phone-number?page=${pageCounter}&perPage=1000&type=${query}`,
              {
                hasToken: token,
                accountUid,
                headers: {
                  Authorization: `Bearer ${token && token.accessToken}`,
                },
              }
            )
          );
          if (error) {
            resolve({
              data: null,
              error,
            });
          } else {
            phoneNumbers.push(...data);
          }
        }
        resolve({
          data: phoneNumbers,
          error: null,
        });
      } else {
        resolve({
          data: phoneNumbers,
          error: null,
        });
      }
    }
  });
};

const updatePhoneNumber = async (
  accountUid = '',
  phoneNumberId = '',
  token = '',
  payload = {},
  rateLimitReachedCallback
) => {
  return new Promise(async (resolve, reject) => {
    const { data, error } = await rcApiResponseFormatter(
      jwksApi.put(
        `${RC_PRODUCTION_URL}/account/${accountUid}/phone-number/${phoneNumberId}`,
        payload,
        {
          hasToken: token,
          accountUid,
          hooks: { rateLimitReached: rateLimitReachedCallback },
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      resolve({
        data,
        error: null,
      });
    }
  });
};

const getDevices = async (accountUid, token, query) => {
  return new Promise(async (resolve, reject) => {
    const phoneNumbers = [];
    let pageCounter = 1;
    const { data, error, paging } = await rcApiResponseFormatter(
      jwksApi.get(
        `${RC_PRODUCTION_URL}/account/${accountUid}/device?perPage=1000${
          query ? `&type=${query}` : ''
        }`,
        {
          hasToken: token,
          accountUid,
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      phoneNumbers.push(...data);
      if (paging.totalPages > 1) {
        while (pageCounter <= paging.totalPages) {
          pageCounter += 1;
          const { data, error } = await rcApiResponseFormatter(
            jwksApi.get(
              `${RC_PRODUCTION_URL}/account/${accountUid}/device?page=${pageCounter}&perPage=1000&type=${query}`,
              {
                hasToken: token,
                accountUid,
                headers: {
                  Authorization: `Bearer ${token && token.accessToken}`,
                },
              }
            )
          );
          if (error) {
            resolve({
              data: null,
              error,
            });
          } else {
            phoneNumbers.push(...data);
          }
        }
        resolve({
          data: phoneNumbers,
          error: null,
        });
      } else {
        resolve({
          data: phoneNumbers,
          error: null,
        });
      }
    }
  });
};

export default {
  getAccountJwksToken,
  getAllExtensions,
  getPhoneNumbers,
  updatePhoneNumber,
  getDevices,
};
